import React, {useMemo, useRef, useState} from 'react';
import {Box, InputAdornment, Typography} from '@mui/material';
import _ from 'lodash';
import {ReactComponent as ErrorIcon} from '../../../../assests/svg/error-field-icon.svg';
import AurButton from '../../../common/aur-button/AurButton';
import {AurTextField} from '../../../common/aur-text-field/AurTextField';
import AurDialog from '../../../common/aur-dialog/AurDialog';
import OverlayUpload from './OverlayUpload';
import {t} from 'i18next';
import OverlayOverview from './OverlayOverview';
import {createOverlay} from '../../../../api/EcaManagementService';
import {RootState} from '../../../../redux/store';
import {useSelector} from 'react-redux';
import {useNavigate} from 'react-router-dom';
import {useToast} from '../../../../contexts/toastContext/ToastContext';
import CreateOverlayErrorAlert from './CreateOverlayErrorAlert';
import {AxiosError} from 'axios';
import {
  EMAIL_ADDRESS_REGEX,
  OVERLAY_NAME_REGEX,
} from '../../../../constants/constants';
import {showErrorToast} from '../../../../api/utils';

const CreateOverlay = () => {
  const [notifyEmailDialogOpen, setNotifyEmailDialogOpen] = useState(false);
  const [notifyEmails, setNotifyEmails] = useState('');
  const [overlayName, setOverlayName] = useState('');
  const [overlayNameError, setOverlayNameError] = useState<string | null>(null);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [filePath, setFilePath] = useState('');
  const [notifyEmailError, setNotifyEmailError] = useState<string | null>(null);
  const [, setFileError] = useState<string>(''); // New state for file error
  const [validationError, setValidationError] = useState<{
    show: boolean;
    errorCodes: string[];
  }>({show: false, errorCodes: []});
  const resetFileUploader = useRef<() => void | null>(null);

  const navigate = useNavigate();
  const {showToast} = useToast();

  const handleDuplicateFileError = (isDuplicate: boolean) => {
    if (isDuplicate) {
      setOverlayNameError(t('overlay.duplicateError'));
    }
  };

  const isNextButtonDisabled = useMemo(() => {
    if (
      !!overlayNameError ||
      !uploadSuccess ||
      (overlayName ?? '').trim().length === 0
    )
      return true;
    return false;
  }, [overlayNameError, uploadSuccess, overlayName]);

  const resetFormToInitial = () => {
    resetFileUploader?.current?.();
    setOverlayName('');
    setFilePath('');
    setUploadSuccess(false);
    setOverlayNameError(null);
    setNotifyEmailError(null);
    setNotifyEmails('');
  };

  const handleTextFieldChange = (event: any) => {
    const value = event.target.value;
    setNotifyEmails(value);
    validateEmails(value);
  };

  const handleFileUploadError = (message: string, field: string) => {
    if (field === 'file') {
      setFileError(message);
    } else if (field === 'fileName') {
      setOverlayNameError(message);
    }
  };
  const handleOverlayNameChange = (event: any) => {
    const value = event.target.value
      .replace(/[\r\n]/g, '') // Remove CR, LF
      .trim();

    const maxLength = 50;

    if (value.length > maxLength) {
      setOverlayNameError(t('overlay.nameLimitErrorMsg', {count: maxLength}));
      return;
    }

    setOverlayName(value);

    if (!OVERLAY_NAME_REGEX.test(value)) {
      setOverlayNameError(t('overlay.letterNumberError'));
      return;
    }
    setOverlayNameError(null);
  };

  const ClientId = useSelector(
    (state: RootState) => state.selectedClient
  ).clientId;
  const {projectId: selectedProjectId} = useSelector(
    (state: RootState) => state.reports
  );

  const validateEmails = (input: string) => {
    const emails = input.split(/[ \s]+/).map((email) => email.trim());
    let valid = true;
    for (const email of emails) {
      if (email && !EMAIL_ADDRESS_REGEX.test(email)) {
        setNotifyEmailError(t('common.validation.invalidEmailList'));
        valid = false;
        break;
      }
    }

    if (valid) {
      setNotifyEmailError(null);
    }
  };

  const handleCreateOverlay = async () => {
    try {
      const requestBody = {
        overlayName: overlayName,
        overlayFilePath: filePath,
        emailAddresses: notifyEmails,
      };

      const paramString = `clientId=${ClientId}&projectId=${selectedProjectId}`;
      const response = await createOverlay(paramString, requestBody);
      const res = response?.data;

      if (res?.data === 'Success') {
        navigate('/eci-home-page/manage-overlays/');
        showToast(
          `${overlayName} ${t('overlay.CreatedOverlayMessage')} `,
          'success',
          3000,
          'center'
        );
      }
    } catch (e: unknown) {
      const error = e as AxiosError;
      const validationErrors: {field: string; message: string[]}[] = _.get(
        error,
        'response.data.validationErrors',
        []
      );
      // If status code is 400 and have validation error then display validation error dialog with error code
      if (validationErrors.length > 0 && error.status === 400) {
        setNotifyEmailDialogOpen(false);
        setValidationError({
          show: true,
          errorCodes: validationErrors.map((item) => item.field),
        });
        return;
      }
      showErrorToast(showToast, error, 'right');
    }
  };

  const handleUploadComplete = (path: string) => {
    setFilePath(path);
    setUploadSuccess(true);
  };

  return (
    <div className="c-overlay">
      <div className={'breadcrumb-section'}>
        <OverlayOverview />
      </div>
      <div className="sub-header">
        <Typography className="typo-text headline1_size color-fc">
          {t('overlay.CreateNewOverlay')}
        </Typography>
      </div>

      <div className="form-section">
        <Typography className="typo-text headline3_size color-fc">
          {t('overlay.letStartWithBasic')}
        </Typography>

        <div className="form-fields">
          <Typography className="typo-text subhead4_size color-fc">
            {t('overlay.nameOverlay')}
          </Typography>
          <div className="text-field-container">
            <AurTextField
              className={`custom-textarea ${overlayNameError ? 'error-border' : ''}`}
              aria-label={t('overlay.nameOverlay')}
              value={overlayName}
              onChange={handleOverlayNameChange}
              placeholder={t('overlay.enterOverlayName')}
              error={!!overlayNameError}
              data-testid="overlay-name-input"
              helperText={overlayNameError}
              aria-describedby="overlayNameHint"
              InputProps={{
                endAdornment: overlayNameError ? (
                  <InputAdornment position="end">
                    <ErrorIcon className="error-icon" />
                  </InputAdornment>
                ) : null,
              }}
            />
          </div>

          {!overlayNameError && (
            <Typography className="info-text" id="overlayNameHint">
              {t('overlay.maxCountError')}
            </Typography>
          )}

          <div className="file-upload-section">
            <Typography className="typo-text subhead4_size color-fc">
              {t('overlay.uploadDatFile')}
            </Typography>

            <div className="file-field">
              <Typography className="file-label">
                {t('overlay.selectDatFile')}
              </Typography>
              <OverlayUpload
                uploadUrl=""
                onUploadComplete={handleUploadComplete}
                overlayName={overlayName}
                onDuplicateFileError={handleDuplicateFileError}
                onError={handleFileUploadError}
                resetUploadState={resetFileUploader}
              />
            </div>
          </div>
        </div>

        <Box
          className="action-buttons"
          display={'flex'}
          justifyContent={'flex-end'}
          gap={2}
        >
          <AurButton
            type="secondary"
            label={t('overlay.cancel')}
            classNames="cancel-button-overlay"
            onClick={() => {
              navigate('/eci-home-page/manage-overlays/');
            }}
            size="Medium"
          />
          <AurButton
            label={t('overlay.next')}
            type="continue"
            size="Medium"
            classNames="next-button-overlay"
            onClick={() => {
              setNotifyEmailDialogOpen(true);
            }}
            disabled={isNextButtonDisabled}
          />
        </Box>

        <AurDialog
          dismissable={false}
          popupTitle={t('overlay.notifyComplete')}
          popupContent={
            <div className="email-dialog info-modify">
              <Typography className="typo-text subhead4_size">
                {t('overlay.includeEmailMessage')}
              </Typography>
              <AurTextField
                value={notifyEmails}
                onChange={handleTextFieldChange}
                fullWidth
                label={t('overlay.emailAdress')}
                required
                error={!!notifyEmailError}
                helperText={notifyEmailError}
                inputProps={{
                  tabIndex: 1,
                }}
                ariaLabel={t('overlay.emailAdress')}
              />
              <Typography
                variant="body2"
                color="textSecondary"
                className="space-top color-css"
              >
                {t('overlay.addEmail')}
              </Typography>
            </div>
          }
          labelActionOk={t('overlay.continue')}
          labelActionCancel={t('overlay.cancel')}
          onOk={() => {
            handleCreateOverlay();
          }}
          onCancel={() => {
            setNotifyEmailDialogOpen(false);
            setNotifyEmails('');
            setNotifyEmailError(null);
          }}
          showDialog={notifyEmailDialogOpen}
          buttonStyle="sideBySide-email"
          onOkDisabled={notifyEmails.trim() === '' || notifyEmailError !== null}
          okActionTabIndex={2}
          cancelActionTabIndex={3}
        />

        <CreateOverlayErrorAlert
          showDialog={validationError.show}
          onOk={() => {
            resetFormToInitial();
            window.scrollTo({
              top: 0,
              left: 0,
              behavior: 'smooth',
            });
            setValidationError({show: false, errorCodes: []});
          }}
          validationErrorCode={validationError.errorCodes}
        />
      </div>
    </div>
  );
};

export default CreateOverlay;
